import React from "react";
import { BADGE_META } from "./swachhataContent";
import { Confetti, useCountUp, fmtDuration } from "./SwachhataBits";
import ecoLogo from "../../../assets/images/Logo-2.png";

const R = 96;
const C = 2 * Math.PI * R;

export function ScoreRing({ score, total }) {
  const v = useCountUp(score, { duration: 1300 });
  const pct = total ? score / total : 0;
  return (
    <div className="shs-ring">
      <svg viewBox="0 0 220 220">
        <defs>
          <linearGradient id="shsRingGrad" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#1b7f3b" />
            <stop offset="0.6" stopColor="#2fb457" />
            <stop offset="1" stopColor="#f28c28" />
          </linearGradient>
        </defs>
        <circle className="shs-ring__track" cx="110" cy="110" r={R} />
        <circle className="shs-ring__bar" cx="110" cy="110" r={R} strokeDasharray={C} strokeDashoffset={C * (1 - pct)} />
      </svg>
      <div className="shs-ring__value">
        <div>
          <b>{v}<span style={{ fontSize: 22, color: "var(--shs-muted)" }}>/{total}</span></b>
          <small>{Math.round(pct * 100)}% correct</small>
        </div>
      </div>
    </div>
  );
}

// `certificateMinScore` (public student quiz): a certificate is issued only at
// or above it; below it the result explains that and offers `onRetry`.
export function ResultView({ participation, passMark = 6, certificateMinScore, onReview, onClose, onReadAgain, onCertificate, onRetry, certificateLabel = "🎓 Get my certificate", fresh = false, subtitle, resultTitle = "Your school's result", closeLabel = "Go to Dashboard" }) {
  const quiz = participation?.quiz || {};
  const school = participation?.school || {};
  const score = quiz.score ?? 0;
  const total = quiz.total ?? 10;
  const badge = BADGE_META[quiz.badge] || BADGE_META.keep_learning;
  const review = quiz.review || [];
  const wrong = review.filter((r) => !r.isCorrect && r.selected).length;
  const skipped = review.filter((r) => !r.selected).length;
  const certMode = certificateMinScore != null;
  const passed = score >= (certMode ? certificateMinScore : passMark);

  return (
    <div className="shs-result">
      <Confetti run={fresh && passed} />
      <span className="shs-badge-pill"><img src={ecoLogo} alt="" />Swachhata Hi Seva · Quiz result</span>
      <h2 className="shs-slide__title" style={{ marginTop: 14 }}>
        {fresh ? (passed ? "Congratulations!" : "Quiz submitted") : resultTitle}
      </h2>
      <p className="shs-result__school">{subtitle != null ? subtitle : `${school.school_name || ""}${school.district ? ` · ${school.district}` : ""}${school.state ? `, ${school.state}` : ""}`}</p>

      <div className="shs-result__grid">
        <ScoreRing score={score} total={total} />
        <div>
          <div className="shs-badge" style={{ "--c": badge.colour }}>
            <span className="shs-badge__emoji">{badge.emoji}</span>
            <span>
              <span className="shs-badge__label">{badge.label}</span>
              <div className="shs-badge__blurb">{badge.blurb}</div>
            </span>
          </div>
          <div className="shs-result__stats">
            <div className="shs-rstat" style={{ "--i": 0, "--c": "#1b7f3b" }}><b>{score}</b><span>Correct</span></div>
            <div className="shs-rstat" style={{ "--i": 1, "--c": "#d93a3a" }}><b>{wrong}</b><span>Incorrect</span></div>
            <div className="shs-rstat" style={{ "--i": 2, "--c": "#f28c28" }}><b>{skipped}</b><span>Skipped</span></div>
            <div className="shs-rstat" style={{ "--i": 3 }}><b>{fmtDuration(quiz.durationSec)}</b><span>Time taken</span></div>
          </div>
          {quiz.submittedAt && (
            <p style={{ marginTop: 10, fontSize: 12.5, color: "var(--shs-muted)" }}>
              Submitted on {new Date(quiz.submittedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              {certMode ? ` · Certificate from ${certificateMinScore}/${total}` : ` · Pass mark ${passMark}/${total}`}
            </p>
          )}
        </div>
      </div>

      {certMode && !passed && (
        <div className="shs-error" style={{ marginTop: 16 }}>
          {`A participation certificate needs at least ${certificateMinScore} correct answers out of ${total}. Review the correct answers below${onRetry ? ", then try the quiz again" : ""}.`}
        </div>
      )}

      <div className="shs-result__actions">
        {onCertificate && (
          <button type="button" className="shs-btn shs-btn--orange shs-btn--pulse" onClick={onCertificate}>{certificateLabel}</button>
        )}
        {certMode && !passed && onRetry && (
          <button type="button" className="shs-btn shs-btn--orange" onClick={onRetry}>Try the quiz again</button>
        )}
        <button type="button" className={`shs-btn ${onCertificate ? "shs-btn--light" : "shs-btn--primary"}`} onClick={onReview}>Review answers</button>
        <button type="button" className="shs-btn shs-btn--light" onClick={onReadAgain}>Read campaign information</button>
        <button type="button" className="shs-btn shs-btn--ghost" onClick={onClose}>{closeLabel}</button>
      </div>
    </div>
  );
}

export function ReviewView({ review = [], onBack, onClose, closeLabel = "Go to Dashboard" }) {
  const KEYS = ["A", "B", "C", "D"];
  return (
    <div className="shs-review">
      <div className="shs-review__head">
        <div>
          <span className="shs-slide__kicker">Answer review</span>
          <h2 className="shs-slide__title" style={{ fontSize: 26 }}>Your 10 questions</h2>
        </div>
        <div className="shs-footer__actions">
          <button type="button" className="shs-btn shs-btn--ghost shs-btn--sm" onClick={onBack}>← Back to result</button>
          <button type="button" className="shs-btn shs-btn--primary shs-btn--sm" onClick={onClose}>{closeLabel}</button>
        </div>
      </div>
      <div className="shs-review__list">
        {review.map((r, i) => {
          const state = r.isCorrect ? "is-correct" : r.selected ? "is-wrong" : "is-skipped";
          return (
            <div key={r.id} className={`shs-review__item ${state}`} style={{ "--i": i }}>
              <div className="shs-review__top">
                <span>Q{r.n} · {r.category}</span>
                <span>{r.isCorrect ? "✔ Correct" : r.selected ? "✘ Incorrect" : "— Skipped"}</span>
              </div>
              <div className="shs-review__q">{r.q}</div>
              <div className="shs-review__opts">
                {KEYS.map((k) => (
                  <div key={k} className={`shs-review__opt ${r.correct === k ? "is-correct" : ""} ${r.selected === k ? "is-selected" : ""}`}>
                    <b>{k}</b><span>{r.options?.[k]}</span>
                    {r.correct === k && <span style={{ marginLeft: "auto", fontSize: 12 }}>Correct answer</span>}
                    {r.selected === k && r.correct !== k && <span style={{ marginLeft: "auto", fontSize: 12 }}>Your answer</span>}
                  </div>
                ))}
              </div>
              {r.explain && <div className="shs-review__explain"><span>💡</span><span>{r.explain}</span></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
